import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  ACCESS_STORAGE_KEY,
  canGuestAccessSite,
  findGuestByEmail,
  isValidEmailFormat,
  persistVerifiedGuest,
} from './guestAllowlist';
import { HoofyCow } from './HoofyCow';

const GATE_BG = '#D5B5A8';

const DEBUG_PARAM = 'gateDebug';

function parseGateDebug() {
  if (typeof window === 'undefined') return null;
  try {
    const v = new URLSearchParams(window.location.search).get(DEBUG_PARAM);
    if (!v) return null;
    const allowed = new Set(['splash', 'entered', 'error', 'success']);
    return allowed.has(v) ? v : null;
  } catch {
    return null;
  }
}

/** Larger when short; steps down as the address grows so it stays on one line. */
function emailGateInputFontSize(charLength) {
  const n = charLength;
  if (n <= 22) return 'clamp(1.28rem, 5.8vw, 1.72rem)';
  if (n <= 32) return 'clamp(1.1rem, 5vw, 1.45rem)';
  if (n <= 42) return 'clamp(0.98rem, 4.4vw, 1.22rem)';
  return 'clamp(0.86rem, 3.8vw, 1.06rem)';
}

function stripGateDebugFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(DEBUG_PARAM)) return;
  url.searchParams.delete(DEBUG_PARAM);
  window.history.replaceState({}, '', url.pathname + url.search + url.hash);
}

/**
 * Full-screen gate before the wedding site.
 * Dev: ?gateDebug=splash|entered|error|success and bottom debug bar.
 */
export function EmailGate({ onGranted, registryLoading = false }) {
  const labelId = useId();
  const thanksId = useId();
  const inputId = useId();
  const isDev = process.env.NODE_ENV === 'development';

  const [phase, setPhase] = useState('form');
  const [email, setEmail] = useState('');
  const [showError, setShowError] = useState(false);
  const [thanksName, setThanksName] = useState('');
  const [debugForced, setDebugForced] = useState(null);
  const [exiting, setExiting] = useState(false);
  const gateRef = useRef(null);

  const SUCCESS_HOLD_MS = 1050;
  const EXIT_ANIM_MS = 560;
  const FORM_TO_SUCCESS_MS = 400;

  const [successExitPending, setSuccessExitPending] = useState(false);
  const shakeWrapRef = useRef(null);

  const playInputShake = useCallback(() => {
    const el = shakeWrapRef.current;
    if (!el) return;
    el.classList.remove('email-gate__input-shake-wrap--shake');
    void el.offsetWidth;
    el.classList.add('email-gate__input-shake-wrap--shake');
  }, []);

  /** Lift footer above iOS virtual keyboard using Visual Viewport API. */
  useEffect(() => {
    if (phase !== 'form') return;
    const root = gateRef.current;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!root || !vv) return;

    const syncKeyboardInset = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--email-gate-vv-inset', `${inset}px`);
    };

    syncKeyboardInset();
    vv.addEventListener('resize', syncKeyboardInset);
    vv.addEventListener('scroll', syncKeyboardInset);
    window.addEventListener('resize', syncKeyboardInset);

    return () => {
      root.style.removeProperty('--email-gate-vv-inset');
      vv.removeEventListener('resize', syncKeyboardInset);
      vv.removeEventListener('scroll', syncKeyboardInset);
      window.removeEventListener('resize', syncKeyboardInset);
    };
  }, [phase]);

  useEffect(() => {
    const d = isDev ? parseGateDebug() : null;
    if (!d) return;
    stripGateDebugFromUrl();
    setDebugForced(d);
    setShowError(false);
    if (d === 'splash') {
      setEmail('');
      setPhase('form');
    } else if (d === 'entered') {
      setEmail('ben.silvertown@gmail.com');
      setPhase('form');
    } else if (d === 'error') {
      setEmail('not.on.list@example.com');
      setShowError(true);
      setPhase('form');
      window.setTimeout(() => playInputShake(), 80);
    } else if (d === 'success') {
      setThanksName('Ben');
      setPhase('success');
    }
  }, [isDev, playInputShake]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const hasInput = email.trim().length > 0;
  const showErrorState = showError || debugForced === 'error';
  const inputFontSize = useMemo(() => emailGateInputFontSize(email.length), [email.length]);

  const messageText = registryLoading
    ? 'one moment…'
    : showErrorState
      ? "oops, are you sure that's the right email?"
      : 'pop in your email';

  const finish = useCallback(() => {
    onGranted?.();
  }, [onGranted]);

  useEffect(() => {
    if (phase !== 'success') {
      setExiting(false);
      return;
    }
    const t = window.setTimeout(() => setExiting(true), SUCCESS_HOLD_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (!exiting) return;
    const t = window.setTimeout(finish, EXIT_ANIM_MS);
    return () => window.clearTimeout(t);
  }, [exiting, finish]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setDebugForced(null);
    if (registryLoading) return;
    if (!isValidEmailFormat(email)) {
      setShowError(true);
      playInputShake();
      return;
    }
    const guest = findGuestByEmail(email);
    if (!guest || !canGuestAccessSite(guest)) {
      setShowError(true);
      playInputShake();
      return;
    }
    setShowError(false);
    persistVerifiedGuest(email);
    setThanksName(guest.firstName);
    setSuccessExitPending(true);
    window.setTimeout(() => {
      setPhase('success');
      setSuccessExitPending(false);
    }, FORM_TO_SUCCESS_MS);
  };

  const applyDebug = (key) => {
    setDebugForced(key === 'reset' ? null : key);
    setShowError(false);
    if (key !== 'success') {
      setSuccessExitPending(false);
    }
    if (key === 'splash' || key === 'reset') {
      setEmail('');
      setPhase('form');
    } else if (key === 'entered') {
      setEmail('ben.silvertown@gmail.com');
      setPhase('form');
    } else if (key === 'error') {
      setEmail('not.on.list@example.com');
      setShowError(true);
      setPhase('form');
      window.setTimeout(() => playInputShake(), 0);
    } else if (key === 'success') {
      setThanksName('Ben');
      setSuccessExitPending(true);
      window.setTimeout(() => {
        setPhase('success');
        setSuccessExitPending(false);
      }, FORM_TO_SUCCESS_MS);
    }
    if (key === 'reset') {
      sessionStorage.removeItem(ACCESS_STORAGE_KEY);
    }
  };

  return (
    <div
      ref={gateRef}
      className={
        'email-gate' +
        (isDev ? ' email-gate--dev' : '') +
        (exiting ? ' email-gate--exiting' : '')
      }
      style={{ backgroundColor: GATE_BG }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={phase === 'success' ? thanksId : labelId}
    >
      {isDev ? (
        <div className="email-gate__debug" aria-label="Email gate test controls (development only)">
          <span className="email-gate__debug-label">Test UI:</span>
          {[
            ['splash', 'Splash'],
            ['entered', 'Entered'],
            ['error', 'Error'],
            ['success', 'Success'],
            ['reset', 'Reset'],
          ].map(([k, lab]) => (
            <button key={k} type="button" className="email-gate__debug-btn" onClick={() => applyDebug(k)}>
              {lab}
            </button>
          ))}
          <span className="email-gate__debug-hint">?gateDebug=splash|entered|error|success</span>
        </div>
      ) : null}
      {phase === 'form' || phase === 'success' ? (
        <div className="email-gate__center">
          <HoofyCow
            className="email-gate__cow email-gate-entrance email-gate-entrance--cow"
            aria-hidden="true"
          />
          <div className="email-gate__center-message-slot">
            {phase === 'form' ? (
              <div
                className={
                  'email-gate__center-copy' +
                  (successExitPending ? ' email-gate__center-copy--exit' : '')
                }
              >
                <p
                  id={labelId}
                  className={[
                    'email-gate__prompt',
                    'email-gate-entrance',
                    'email-gate-entrance--prompt',
                    showErrorState && 'email-gate__prompt--error',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-live="polite"
                >
                  <span key={messageText} className="email-gate__prompt-line">
                    {messageText}
                  </span>
                </p>
              </div>
            ) : (
              <p
                id={thanksId}
                className="email-gate__thanks email-gate-entrance email-gate-entrance--thanks"
                role="status"
              >
                Thanks,
                <br />
                <span className="email-gate__thanks-name">{thanksName}</span>
              </p>
            )}
          </div>
        </div>
      ) : null}
      {phase === 'form' ? (
        <>
          <form
            className={
              'email-gate__footer' + (successExitPending ? ' email-gate__footer--exit' : '')
            }
            onSubmit={handleSubmit}
            noValidate
          >
            <label htmlFor={inputId} className="visually-hidden">
              Email address
            </label>
            <div
              ref={shakeWrapRef}
              className="email-gate__input-shake-wrap"
              onAnimationEnd={(ev) => {
                if (
                  ev.target === ev.currentTarget &&
                  ev.animationName === 'emailGateInputShake'
                ) {
                  ev.currentTarget.classList.remove('email-gate__input-shake-wrap--shake');
                }
              }}
            >
              <input
                id={inputId}
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                enterKeyHint="go"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="your@email.com"
                value={email}
                disabled={registryLoading}
                onChange={(ev) => {
                  setEmail(ev.target.value);
                  if (showError) setShowError(false);
                }}
                style={{ fontSize: inputFontSize }}
                className={[
                  'email-gate__input',
                  'email-gate-entrance',
                  'email-gate-entrance--input',
                  hasInput && 'email-gate__input--filled',
                  showErrorState && 'email-gate__input--error',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            </div>
            <button
              type="submit"
              disabled={registryLoading}
              className="travel-section__maps-btn email-gate__maps-btn email-gate-entrance email-gate-entrance--submit"
            >
              <span className="travel-section__maps-btn__ring" aria-hidden="true">
                <svg
                  className="travel-section__maps-btn__svg"
                  viewBox="0 0 240 48"
                  preserveAspectRatio="none"
                  width="100%"
                  height="100%"
                >
                  <ellipse
                    className="travel-section__maps-btn__ellipse"
                    cx="120"
                    cy="24"
                    rx="115"
                    ry="21"
                    fill="transparent"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    vectorEffect="nonScalingStroke"
                  />
                </svg>
              </span>
              Enter
            </button>
          </form>
        </>
      ) : null}
    </div>
  );
}
